import type React from "react";
import { useState } from "react";

// Mirrors the backend payload from
// agent_runtime/recommended_actions.py:serialize_actions.
export interface RecommendedActionParam {
  key: string;
  label?: string;
  control?: "number" | "slider" | "select";
  value?: number | string;
  default?: number | string;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  options?: (string | number)[];
}

export interface RecommendedActionItem {
  id: string;
  category: "diagnostic" | "medication" | "referral_followup" | "note_only";
  lens?: string;
  title: string;
  rationale?: string;
  target_section?: string;
  params?: RecommendedActionParam[];
  state: "pending" | "accepted" | "ignored" | "applied";
}

export interface RecommendedActionsData {
  audio_file_id: number;
  actions: RecommendedActionItem[];
  count: number;
}

type CustomAction = { type: string; payload?: Record<string, unknown> };

interface RecommendedActionsWidgetProps {
  data: RecommendedActionsData;
  onCustomAction: (action: CustomAction) => void;
  disabled?: boolean;
}

// Color is a thin LEFT-ACCENT only (not a fill) so a stack of cards stays
// calm. Muted, professional hues. label = short category tag.
const CATEGORY_STYLE: Record<RecommendedActionItem["category"], { label: string; accent: string }> = {
  diagnostic: { label: "Order", accent: "#2563EB" },
  medication: { label: "Med", accent: "#059669" },
  referral_followup: { label: "Referral", accent: "#D97706" },
  note_only: { label: "Note", accent: "#7C3AED" },
};

// How many pending recommendations to show before "Show N more".
const VISIBLE_LIMIT = 4;

const pkey = (actionId: string, key: string) => `${actionId}::${key}`;

const RecommendedActionsWidget: React.FC<RecommendedActionsWidgetProps> = ({ data, onCustomAction, disabled }) => {
  const actions = data?.actions || [];
  const [paramValues, setParamValues] = useState<Record<string, number | string>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [showAll, setShowAll] = useState(false);

  if (!data || actions.length === 0) return null;

  const valueFor = (action: RecommendedActionItem, p: RecommendedActionParam): number | string => {
    const k = pkey(action.id, p.key);
    if (k in paramValues) return paramValues[k];
    return (p.value ?? p.default ?? "") as number | string;
  };
  const setValue = (actionId: string, key: string, value: number | string) =>
    setParamValues((prev) => ({ ...prev, [pkey(actionId, key)]: value }));

  const accept = (action: RecommendedActionItem) => {
    if (disabled) return;
    const params = (action.params || []).map((p) => ({ key: p.key, value: valueFor(action, p) }));
    onCustomAction({
      type: "accept_recommended_action",
      payload: { audio_file_id: data.audio_file_id, action_id: action.id, params },
    });
  };
  const ignore = (action: RecommendedActionItem) => {
    if (disabled) return;
    onCustomAction({
      type: "ignore_recommended_action",
      payload: { audio_file_id: data.audio_file_id, action_id: action.id },
    });
  };

  const pending = actions.filter((a) => a.state === "pending");
  const resolved = actions.filter((a) => a.state !== "pending");
  const shownPending = showAll ? pending : pending.slice(0, VISIBLE_LIMIT);
  const hiddenCount = pending.length - shownPending.length;
  const acceptedCount = actions.filter((a) => a.state === "accepted").length;

  const renderRow = (action: RecommendedActionItem) => {
    const style = CATEGORY_STYLE[action.category] || CATEGORY_STYLE.note_only;
    const isResolved = action.state !== "pending";
    const hasParams = (action.params?.length ?? 0) > 0;
    const isOpen = !!expanded[action.id];
    return (
      <div key={action.id} style={{ ...styles.row, borderLeftColor: style.accent, opacity: isResolved ? 0.6 : 1 }}>
        <div style={styles.rowTop}>
          <span style={{ ...styles.cat, color: style.accent }}>{style.label}</span>
          <span style={styles.title}>{action.title}</span>
        </div>

        {isResolved ? (
          <span style={{ ...styles.resolved, color: action.state === "ignored" ? "#9CA3AF" : style.accent }}>
            {action.state === "ignored" ? "Ignored" : action.state === "applied" ? "✓ Applied" : "✓ Accepted"}
          </span>
        ) : (
          <div style={styles.actions}>
            <button
              type="button"
              disabled={disabled}
              onClick={() => accept(action)}
              style={{ ...styles.accept, backgroundColor: style.accent }}
            >
              Accept
            </button>
            <button type="button" disabled={disabled} onClick={() => ignore(action)} style={styles.ignore}>
              Ignore
            </button>
            {hasParams || action.rationale ? (
              <button
                type="button"
                onClick={() => setExpanded((p) => ({ ...p, [action.id]: !p[action.id] }))}
                style={styles.adjust}
              >
                {isOpen ? "Hide" : hasParams ? "Adjust" : "Why?"}
              </button>
            ) : null}
          </div>
        )}

        {isOpen && !isResolved ? (
          <div style={styles.detail}>
            {action.rationale ? <div style={styles.rationale}>{action.rationale}</div> : null}
            {action.params?.map((p) => (
              <ParamControl
                key={p.key}
                param={p}
                value={valueFor(action, p)}
                accent={style.accent}
                disabled={disabled}
                onChange={(v) => setValue(action.id, p.key, v)}
              />
            ))}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <span style={styles.headerText}>Recommended actions</span>
        <span style={styles.count}>{pending.length || actions.length}</span>
      </div>
      {shownPending.map(renderRow)}
      {hiddenCount > 0 ? (
        <button type="button" onClick={() => setShowAll(true)} style={styles.showMore}>
          Show {hiddenCount} more
        </button>
      ) : null}
      {resolved.map(renderRow)}
      {acceptedCount > 0 ? (
        <button
          type="button"
          disabled={disabled}
          onClick={() => onCustomAction({ type: "apply_staged_edits", payload: { audio_file_id: data.audio_file_id } })}
          style={{ ...styles.apply, ...(disabled ? styles.disabled : {}) }}
        >
          Apply {acceptedCount} accepted {acceptedCount === 1 ? "change" : "changes"} → regenerate note
        </button>
      ) : null}
    </div>
  );
};

const ParamControl: React.FC<{
  param: RecommendedActionParam;
  value: number | string;
  accent: string;
  disabled?: boolean;
  onChange: (v: number | string) => void;
}> = ({ param, value, accent, disabled, onChange }) => {
  const label = param.label || param.key;
  if (param.control === "select" && param.options?.length) {
    return (
      <label style={styles.paramRow}>
        <span style={styles.paramLabel}>{label}</span>
        <select
          disabled={disabled}
          value={String(value)}
          onChange={(e) => onChange(e.target.value)}
          style={styles.input}
        >
          {param.options.map((opt) => (
            <option key={String(opt)} value={String(opt)}>
              {String(opt)}
            </option>
          ))}
        </select>
      </label>
    );
  }
  if (param.control === "slider") {
    return (
      <label style={styles.paramRow}>
        <span style={styles.paramLabel}>{label}</span>
        <input
          type="range"
          disabled={disabled}
          min={param.min}
          max={param.max}
          step={param.step ?? 1}
          value={Number(value) || 0}
          onChange={(e) => onChange(Number(e.target.value))}
          style={styles.slider}
        />
        <span style={{ ...styles.paramValue, color: accent }}>
          {value}
          {param.unit ? ` ${param.unit}` : ""}
        </span>
      </label>
    );
  }
  const isNumber = param.control === "number" || typeof param.default === "number";
  return (
    <label style={styles.paramRow}>
      <span style={styles.paramLabel}>{label}</span>
      <input
        type={isNumber ? "number" : "text"}
        disabled={disabled}
        min={param.min}
        max={param.max}
        step={param.step}
        value={value as string | number}
        onChange={(e) => onChange(isNumber ? Number(e.target.value) : e.target.value)}
        style={styles.input}
      />
      {param.unit ? <span style={styles.paramUnit}>{param.unit}</span> : null}
    </label>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    marginTop: "8px",
    border: "1px solid #E5E7EB",
    borderRadius: "12px",
    backgroundColor: "#FFFFFF",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 12px",
    borderBottom: "1px solid #F3F4F6",
  },
  headerText: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#6B7280",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  count: {
    marginLeft: "auto",
    fontSize: "11px",
    fontWeight: 600,
    color: "#6B7280",
    backgroundColor: "#F3F4F6",
    padding: "1px 7px",
    borderRadius: "999px",
  },
  row: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    padding: "9px 12px 9px 11px",
    borderLeft: "3px solid",
    borderBottom: "1px solid #F3F4F6",
  },
  rowTop: { display: "flex", alignItems: "baseline", gap: "8px", flexWrap: "wrap" },
  cat: { fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.03em", flexShrink: 0 },
  title: { fontSize: "13px", color: "#111827", lineHeight: 1.35 },
  actions: { display: "flex", alignItems: "center", gap: "10px" },
  accept: {
    padding: "3px 12px",
    fontSize: "12px",
    fontWeight: 600,
    color: "#FFFFFF",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  ignore: {
    padding: "3px 4px",
    fontSize: "12px",
    color: "#6B7280",
    background: "none",
    border: "none",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  adjust: {
    marginLeft: "auto",
    padding: "3px 4px",
    fontSize: "12px",
    color: "#4338CA",
    background: "none",
    border: "none",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  resolved: { fontSize: "12px", fontWeight: 600 },
  detail: { display: "flex", flexDirection: "column", gap: "6px", paddingTop: "2px" },
  rationale: { fontSize: "12px", color: "#4B5563", lineHeight: 1.45 },
  paramRow: { display: "flex", alignItems: "center", gap: "8px", fontSize: "12px" },
  paramLabel: { color: "#374151", minWidth: "110px" },
  paramValue: { fontWeight: 600, minWidth: "56px", textAlign: "right" },
  paramUnit: { color: "#6B7280" },
  slider: { flex: 1 },
  input: {
    padding: "3px 6px",
    border: "1px solid #D1D5DB",
    borderRadius: "6px",
    fontFamily: "inherit",
    maxWidth: "240px",
  },
  showMore: {
    width: "100%",
    padding: "8px 12px",
    fontSize: "12px",
    fontWeight: 500,
    color: "#4338CA",
    background: "none",
    border: "none",
    borderBottom: "1px solid #F3F4F6",
    cursor: "pointer",
    fontFamily: "inherit",
    textAlign: "left",
  },
  apply: {
    width: "100%",
    padding: "10px 12px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#FFFFFF",
    backgroundColor: "#4338CA",
    border: "none",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  disabled: { opacity: 0.6, cursor: "not-allowed" },
};

export default RecommendedActionsWidget;
